import React from "react";
import { View, StyleSheet, DimensionValue } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { Skeleton } from "@/components/ui/Skeleton";
import { ColumnDef } from "./types";

interface TableSkeletonBodyProps<T> {
  columns: ColumnDef<T>[];
  rowCount?: number;
  showActionsColumn?: boolean;
  selectable?: boolean;
}

function getColumnStyle(column: ColumnDef<any>) {
  const style: any = {};

  if (column.width) {
    style.width = column.width;
  }
  if (column.minWidth) {
    style.minWidth = column.minWidth;
  }
  if (column.maxWidth) {
    style.maxWidth = column.maxWidth;
  }
  if (column.flex) {
    style.flex = column.flex;
  }
  if (!column.width && !column.flex) {
    style.flex = 1;
  }

  return style;
}

function getSkeletonWidth(column: ColumnDef<any>): DimensionValue {
  // If column has a fixed width, use ~70% of it for skeleton
  if (typeof column.width === "number") {
    return Math.floor(column.width * 0.7);
  }
  // Default widths based on common patterns
  if (column.key === "status") return 80;
  if (column.key === "name" || column.key.includes("name")) return 120;
  if (column.key === "date" || column.key.includes("date") || column.key.includes("_at")) return 100;
  if (column.key === "email" || column.key.includes("email")) return 150;
  // Default
  return "70%";
}

export function TableSkeletonBody<T>({
  columns,
  rowCount = 5,
  showActionsColumn = false,
  selectable = false,
}: TableSkeletonBodyProps<T>) {
  const { colors } = useTheme();

  return (
    <View>
      {Array.from({ length: rowCount }).map((_, rowIndex) => (
        <View
          key={rowIndex}
          style={[
            styles.row,
            { borderBottomColor: colors.border },
            rowIndex % 2 === 1 && { backgroundColor: colors.backgroundSecondary + "40" },
          ]}
        >
          {/* Selection checkbox skeleton */}
          {selectable && (
            <View style={styles.checkboxCell}>
              <Skeleton width={18} height={18} radius={4} />
            </View>
          )}

          {/* Column cells */}
          {columns.map((column) => {
            const columnStyle = getColumnStyle(column);
            const skeletonWidth = getSkeletonWidth(column);

            return (
              <View key={column.key} style={[styles.cell, columnStyle]}>
                <Skeleton width={skeletonWidth} height={14} radius={4} />
              </View>
            );
          })}

          {/* Actions column skeleton */}
          {showActionsColumn && (
            <View style={styles.actionsCell}>
              <Skeleton width={24} height={24} radius={12} />
            </View>
          )}
        </View>
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: StyleSheet.hairlineWidth,
    minHeight: 44,
  },
  cell: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    justifyContent: "center",
  },
  checkboxCell: {
    width: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  actionsCell: {
    width: 60,
    alignItems: "center",
    justifyContent: "center",
  },
});
